import React, { useEffect, useState } from 'react';
import logger from '../utils/vercelRuntimeLogger';

interface DebugPanelProps {
  show?: boolean;
}

const DebugPanel: React.FC<DebugPanelProps> = ({ show = false }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [isVisible, setIsVisible] = useState(show);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    // Update logs every second
    const interval = setInterval(() => {
      const currentLogs = logger.getBufferedLogs();
      setLogs(currentLogs.slice(-20)); // Keep last 20 logs
    }, 1000);

    // Keyboard shortcut listener
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (!isVisible || process.env.NODE_ENV !== 'development') {
    return null;
  }

  const getLogColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-500';
      case 'warn': return 'text-yellow-500';
      case 'info': return 'text-blue-500';
      case 'debug': return 'text-gray-500';
      default: return 'text-white';
    }
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${minimized ? 'w-auto' : 'w-96'}`}>
      <div className="bg-black/90 backdrop-blur-sm rounded-lg shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <span className="text-white text-sm font-mono">Debug Console</span>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setMinimized(!minimized)}
              className="text-gray-400 hover:text-white p-1"
              title={minimized ? "Expand" : "Minimize"}
            >
              {minimized ? '□' : '—'}
            </button>
            <button
              onClick={() => logger.clearBuffer()}
              className="text-gray-400 hover:text-white p-1"
              title="Clear logs"
            >
              ✕
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-white p-1"
              title="Close (Ctrl+Shift+D)"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        {!minimized && (
          <div className="max-h-80 overflow-y-auto p-2 space-y-1">
            {logs.length === 0 ? (
              <div className="text-gray-500 text-xs font-mono p-2 text-center">
                No logs yet. Press Ctrl+Shift+D to toggle.
              </div>
            ) : (
              logs.map((log, index) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className="text-xs font-mono p-1 border-l-2 border-gray-700 hover:border-gray-500 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600 flex-shrink-0">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className={`font-semibold ${getLogColor(log.level)}`}>
                          [{log.level.toUpperCase()}]
                        </span>
                        <span className="text-cyan-400">{log.component}</span>
                      </div>
                      <div className="text-white break-words">{log.action}</div>
                      {log.data && (
                        <details className="mt-1">
                          <summary className="text-gray-400 cursor-pointer hover:text-white">
                            Data
                          </summary>
                          <pre className="text-gray-300 text-xs overflow-x-auto mt-1 p-1 bg-black/50 rounded">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </details>
                      )}
                      {log.error && (
                        <div className="text-red-400 mt-1">
                          Error: {log.error.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Footer */}
        {!minimized && (
          <div className="p-2 border-t border-gray-700 flex justify-between items-center">
            <div className="text-xs text-gray-500">
              Session: {logs[0]?.sessionId?.substring(0, 16)}...
            </div>
            <button
              onClick={() => {
                const logsJson = logger.exportLogs();
                const blob = new Blob([logsJson], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `debug-logs-${Date.now()}.json`;
                a.click();
              }}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Export Logs
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebugPanel;